import { useCallback, useState } from "react";
import { evaluate } from "mathjs";

interface MeasurementValues {
    [key: string]: number;
}

interface PrefilledValues {
    [laborServiceId: string]: {
        value?: string;
        quantity?: number;
    };
}

interface LaborService {
    Id: string;
    Name: string;
    CalculationFormula?: string | null;
}

const useLaborFormulaEvaluator = () => {
    const [computedValues, setComputedValues] = useState<Record<string, number | null>>({});

    const evaluateFormula = useCallback(
        (
            measurements: any[],
            laborServices: LaborService[],
            _prefilledValues: PrefilledValues,
        ): Record<string, number | null> => {
            const results: Record<string, number | null> = {};
            const measurementsObject = convertMeasurementIntoKeyValuePair(measurements);

            laborServices.forEach((service) => {
                const expression = service.CalculationFormula;

                if (expression) {
                    try {
                        let result = evaluate(expression, measurementsObject) as number;

                        // Default to WholeNumber formatting for now unless we add outputFormat to DB later
                        result = Math.ceil(result);

                        results[service.Id] = result;
                    } catch (error) {
                        // console.error(`Error evaluating formula for ${service.Name}:`, error);
                        results[service.Id] = 0;
                    }
                } else {
                    results[service.Id] = 0;
                }
            });

            setComputedValues(results);
            return results;
        },
        [],
    );

    const convertMeasurementIntoKeyValuePair = (measurements: any) => {
        if (!measurements) return getDefaultValues();

        const result = measurements.reduce((acc: any, measurement: any) => {
            const value = measurement.ActualValue ?? measurement.CalculatedValue;
            acc[measurement.MeasurementType.replace(/\s+/g, "")] = Number(value) || 0;
            // Also keep original name just in case
            acc[measurement.MeasurementType] = Number(value) || 0;
            return acc;
        }, {});

        return { ...getDefaultValues(), ...result };
    };

    const getDefaultValues = () => ({
        Area: 0,
        Eave: 0,
        Rake: 0,
        Ridge: 0,
        Hip: 0,
        Valley: 0,
        Headwall: 0,
        Step: 0,
        RollMetal: 0,
        CounterFlashing: 0,
        RoofVent: 0,
        RVs: 0,
        Skylight: 0,
        Skylights: 0,
        Chimney: 0,
        PipeJack: 0,
        Pjack: 0,
        SplitBoots: 0,
        SplitsBoots: 0,
        FurnaceVents: 0,
        Furn: 0,
        BathroomVents: 0,
        BathVents: 0,
        DryerVents: 0,
        GutterLength: 0,
        DownspoutLength: 0,
    });

    return { computedValues, evaluateFormula };
};

export default useLaborFormulaEvaluator;
